import {
  DataProvidersWithPoiOptions,
  PoiOption,
  PoiOptionListResponse,
  PoiOptionResponse,
  PoiSubOptionResponse,
} from 'components/common/types/Planner.types';

const FIRST_CHILD_DEPTH = 1;
const SECOND_CHILD_DEPTH = 2;

export const removeEmptyChildren = (option: PoiOption): PoiOption => {
  return option.children?.length
    ? {
        ...option,
        children: option.children.map(removeEmptyChildren),
      }
    : { ...option, children: undefined };
};

export const splitAndTrimCategories = (categoryType?: string): string[] => {
  return categoryType?.split('/').map((category) => category.trim()) ?? [''];
};

export const createRootCategory = (categoryType: string): PoiOption => {
  const [rootCategoryType] = splitAndTrimCategories(categoryType);

  return {
    categoryType: rootCategoryType,
    categoryPath: rootCategoryType,
    code: rootCategoryType,
    name: rootCategoryType,
    selectable: false,
    children: [],
  };
};

export const buildSubCategoriesHierarchy = (categoryType: string, rootCategory: PoiOption): PoiOption => {
  const categories = splitAndTrimCategories(categoryType);
  let currentParent = rootCategory;

  categories.slice(1).forEach((categoryName, index) => {
    const subCategories = categories.slice(0, index + SECOND_CHILD_DEPTH);
    const subCategoryType = subCategories.join('/');
    const subCategoryCode = subCategories.join('-');

    let subCategory = currentParent.children?.find((child) => child.categoryType === subCategoryType);

    if (!subCategory) {
      subCategory = {
        categoryType: subCategoryType,
        categoryPath: subCategoryType,
        code: subCategoryCode,
        name: categoryName,
        selectable: false,
        parentCode: subCategories[subCategories.length - SECOND_CHILD_DEPTH],
        children: [],
      };

      currentParent.children?.push(subCategory);
    }

    currentParent = subCategory;
  });

  return currentParent;
};

export const processSubOptions = (subOptions: PoiSubOptionResponse[], categoryType: string): PoiOption[] => {
  return subOptions.map(({ categoryName, suboptions }) => {
    const categories = splitAndTrimCategories(categoryType);

    const baseOption: PoiOption = {
      code: `${categoryName}-${categoryType}`,
      name: categoryName,
      categoryName,
      categoryPath: `${categoryType}/${categoryName}`,
      categoryType,
      selectable: !suboptions?.length,
      parentCode:
        categories[categories.length - (suboptions?.length ? SECOND_CHILD_DEPTH : FIRST_CHILD_DEPTH)] || categories[0],
    };

    if (suboptions?.length) {
      return {
        ...baseOption,
        children: processSubOptions(suboptions, categoryType),
      };
    }

    return baseOption;
  });
};

export const createCategoryTypeTree = (
  subOptions: PoiSubOptionResponse[],
  categoryType: string,
  rootCategory: PoiOption,
): void => {
  const lastParent = buildSubCategoriesHierarchy(categoryType, rootCategory);
  lastParent.children = processSubOptions(subOptions, categoryType);
};

export const transformPoiOptions = (poiOptionResponses: PoiOptionResponse[]): PoiOption[] => {
  const categoryMap = new Map<string, PoiOption>();

  poiOptionResponses.forEach(({ categoryType, suboptions }) => {
    const categories = splitAndTrimCategories(categoryType);
    const rootCategoryType = categories[0];

    let rootCategory = categoryMap.get(rootCategoryType);

    if (!rootCategory) {
      rootCategory = createRootCategory(categoryType);
      categoryMap.set(rootCategoryType, rootCategory);
    }

    createCategoryTypeTree(suboptions, categoryType, rootCategory);
  });

  return Array.from(categoryMap.values()).map(removeEmptyChildren);
};

export const transformDataProvidersWithPoiOptions = (
  poiOptionListResponses: PoiOptionListResponse[],
): DataProvidersWithPoiOptions => {
  const result: DataProvidersWithPoiOptions = {};

  poiOptionListResponses.forEach(({ dataProvider, options }) => {
    result[dataProvider] = transformPoiOptions(options);
  });

  return result;
};
